const PLACEHOLDER_PATTERNS = [
  /no detailed information available/i,
  /lorem ipsum/i,
  /dummy/i,
  /placeholder/i,
  /to be filled/i,
];

const SECTOR_MISMATCH_PATTERNS = [
  { key: 'ev_charger', grant: /electric\s+vehicle|ev\s+charger|eccg|ehvcg/i, project: /ev|electric vehicle|charging|fleet electrification/i },
  { key: 'maritime', grant: /maritime|mcf-|talent@maritime/i, project: /maritime|shipping|port|vessel|logistics port/i },
  { key: 'tourism', grant: /tourism|stb|bif|esf/i, project: /tourism|hospitality|visitor|attraction|hotel|f&b venue/i },
  { key: 'agri', grant: /agri|food cluster|act\b/i, project: /agri|food production|farm|aquaculture/i },
];

const GRANT_ALIAS_MAP = {
  scaleupsg: 'scale-up sg',
  smesgodigital: 'smes go digital',
  startupsg: 'startup sg',
  bizadapt: 'business adaptation grant',
  wdg: 'skillsfuture workforce development grant',
  eis: 'enterprise innovation scheme',
  psg: 'psg',
  edg: 'edg',
  mra: 'mra',
  sfec: 'sfec',
  eeg: 'eeg',
};

export function normalizeGrantName(name) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  const cleaned = raw
    .replace(/[()]/g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/[^\w\s*+]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return GRANT_ALIAS_MAP[cleaned] || cleaned;
}

export function getCanonicalGrantKey(grant) {
  const code = String(grant?.grantId || grant?.grant_id || grant?.code || grant?.acronym || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9*+_-]/g, '');
  if (code) {
    if (/^mra($|_|-)|market_readiness_assistance/.test(code)) return 'code:mra';
    if (/^psg($|_|-)|productivity_solutions_grant/.test(code)) return 'code:psg';
    if (/^edg($|_|-)|enterprise_development_grant/.test(code)) return 'code:edg';
    if (/^eeg($|_|-)|energy_efficiency_grant|enterprise_energy_grant/.test(code)) return 'code:eeg';
    if (/^startupsg|^startup_sg|startup_singapore/.test(code)) return 'code:startup_sg';
    if (/^bizadapt|business_adaptation/.test(code)) return 'code:bizadapt';
    if (/^sfec|skillsfuture_enterprise_credit/.test(code)) return 'code:sfec';
    if (/wdg.*jr|job_redesign/.test(code)) return 'code:wdg_jr_plus';
    return `code:${code}`;
  }
  const name = normalizeGrantName(grant?.grantFullName || grant?.grant_full_name || grant?.displayName || grant?.display_name || grant?.name);
  if (name) return `name:${name}`;
  return 'unknown';
}

export function getGrantDisplayName(grantLike) {
  const raw = String(
    grantLike?.grantFullName ||
      grantLike?.grant_full_name ||
      grantLike?.displayName ||
      grantLike?.display_name ||
      grantLike?.name ||
      'Grant'
  )
    .replace(/\s+/g, ' ')
    .trim();
  const compact = raw
    .replace(/^Productivity Solutions Grant\s*\(PSG\)$/i, 'Productivity Solutions Grant')
    .replace(/^Enterprise Development Grant\s*\(EDG\)$/i, 'Enterprise Development Grant')
    .replace(/^SkillsFuture Workforce Development Grant.*\(WDG JR\+\)$/i, 'SkillsFuture WDG JR+')
    .replace(/^Energy Efficiency Grant\s*-\s*Base\s*-\s*EnterpriseSG\s*\(EEG-B ESG\)$/i, 'EEG Base - EnterpriseSG')
    .replace(/^Global Ready Talent Programme\s*-\s*Overseas Allowance\s*\(GRT-OA\)$/i, 'GRT Overseas Allowance');
  return compact.replace(/\|/g, ' ').replace(/\s+/g, ' ').trim();
}

export function formatCurrency(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'Not available';
  return `S$${Math.round(n).toLocaleString('en-SG')}`;
}

export function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'Not available';
  return `${Math.round(n)}%`;
}

function tokenizeText(v) {
  return String(v || '')
    .toLowerCase()
    .replace(/[_-]/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

// Fit bands shared with the backend contract and the results page:
// Strong/High >= 72 · Promising/Medium 60–71 · Partial/Low 45–59 · Weak < 45.
export const FIT_BANDS = Object.freeze({
  strongMin: 72,
  promisingMin: 60,
  partialMin: 45,
});

/** Blocked grants are capped at Low regardless of score. */
export const BLOCKED_RELEVANCE_CAP = FIT_BANDS.partialMin + 14; // 59

export function fitBandLabel(value, { pdf = false } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return pdf ? 'Low' : 'Not evidenced';
  if (n >= FIT_BANDS.strongMin) return pdf ? 'High' : 'Strong';
  if (n >= FIT_BANDS.promisingMin) return pdf ? 'Medium' : 'Promising';
  if (n >= FIT_BANDS.partialMin) return pdf ? 'Low' : 'Partial';
  return pdf ? 'Low' : 'Weak';
}

export function isGrantBlocked(grant) {
  if (!grant || typeof grant !== 'object') return false;
  if (grant.blocked === true) return true;
  const after = String(grant.eligibilityAfterPrerequisites || grant.eligibility_after_prerequisites || '').toLowerCase();
  if (after === 'blocked') return true;
  const checks = grant.prerequisiteChecks || grant.prerequisite_checks;
  return Array.isArray(checks) && checks.some((c) => c && c.blocking && String(c.status || '').toLowerCase() === 'unmet');
}

export function calculateStrategicRelevance(grant, context = {}) {
  const industry = String(context.industry || '').toLowerCase();
  const projectType = Array.isArray(context.projectType)
    ? context.projectType.join(' ').toLowerCase()
    : String(context.projectType || '').toLowerCase();
  const projectScope = String(context.projectScope || '').toLowerCase();
  const grantText = [grant?.grantFullName, grant?.grant_full_name, grant?.name, grant?.displayName, grant?.summary, grant?.purpose].join(' ').toLowerCase();

  let score = 60;
  const grantTokens = new Set(tokenizeText(grantText));
  const projectTokens = new Set(tokenizeText(`${industry} ${projectType} ${projectScope}`));

  let overlap = 0;
  projectTokens.forEach((t) => {
    if (grantTokens.has(t)) overlap += 1;
  });
  score += Math.min(20, overlap * 2);

  for (const rule of SECTOR_MISMATCH_PATTERNS) {
    if (rule.grant.test(grantText) && !rule.project.test(`${projectType} ${projectScope} ${industry}`)) {
      score -= 35;
    }
  }

  if (/psg|edg|mra|sfec|startup sg|scale-up sg|eis|eeg|bizadapt/i.test(grantText)) score += 10;
  if (/legacy|closed|expired/i.test(grantText)) score -= 15;

  // Prefer the engine's own strategic fit when it is present (project_alignment based).
  const backendFit = Number(grant?.strategicFitScore ?? grant?.strategic_fit_score);
  if (Number.isFinite(backendFit)) score = backendFit;

  const bounded = Math.max(0, Math.min(100, score));
  // A grant blocked by a structural prerequisite is capped at Low regardless of score.
  if (isGrantBlocked(grant)) return Math.min(bounded, BLOCKED_RELEVANCE_CAP);
  return bounded;
}

/**
 * Status bands aligned with the shared fit bands:
 * strong_match >= 72/72 (+ confidence >= 70), likely_fit >= 60/60, possible_fit >= 45/45.
 * Blocked grants always classify as `blocked`.
 */
export function classifyGrantStatus({ alignmentScore = 0, strategicRelevanceScore = 0, confidenceScore = 0, blocked = false }) {
  if (blocked) return 'blocked';
  if (strategicRelevanceScore < 40) return 'excluded';
  if (alignmentScore >= FIT_BANDS.strongMin && strategicRelevanceScore >= FIT_BANDS.strongMin && confidenceScore >= 70) return 'strong_match';
  if (alignmentScore >= FIT_BANDS.promisingMin && strategicRelevanceScore >= FIT_BANDS.promisingMin) return 'likely_fit';
  if (alignmentScore >= FIT_BANDS.partialMin && strategicRelevanceScore >= FIT_BANDS.partialMin) return 'possible_fit';
  if (confidenceScore < 45 && alignmentScore < FIT_BANDS.partialMin) return 'excluded';
  return 'low_priority';
}

export function dedupeGrants(grants = []) {
  const bestByKey = new Map();
  for (const item of grants) {
    const key = getCanonicalGrantKey(item.grant || item);
    const existing = bestByKey.get(key);
    if (!existing || item.compositeRankScore > existing.compositeRankScore) {
      bestByKey.set(key, item);
    }
  }
  return Array.from(bestByKey.values());
}

export function mergeGrantVariants(grants = []) {
  const grouped = new Map();
  for (const g of grants) {
    const key = g.canonicalGrantKey || getCanonicalGrantKey(g.grant || g);
    const arr = grouped.get(key) || [];
    arr.push(g);
    grouped.set(key, arr);
  }
  return Array.from(grouped.values()).map((group) =>
    group.sort((a, b) => (b.compositeRankScore || 0) - (a.compositeRankScore || 0))[0]
  );
}

export function estimatePdfPageCount({ mode, expandedCount, lowerPriorityCount, includeDetailedScoringAppendix, includeEvidenceTrace, includeDocumentChecklist }) {
  const normalizedMode = mode === 'compact_advisory' || mode === 'executive'
    ? 'compact_advisory'
    : mode === 'standard_advisory' || mode === 'standard'
      ? 'standard_advisory'
      : 'full_all_grants';

  if (normalizedMode === 'compact_advisory') {
    return 8;
  }
  if (normalizedMode === 'standard_advisory') {
    return 12;
  }
  if (normalizedMode === 'full_all_grants') {
    return 25;
  }

  const base = normalizedMode === 'compact_advisory' ? 4 : normalizedMode === 'standard_advisory' ? 8 : 11;
  const expandedFactor = normalizedMode === 'compact_advisory' ? 0.7 : normalizedMode === 'standard_advisory' ? 1.3 : 2.35;
  const lowerPriorityFactor = normalizedMode === 'full_all_grants' ? 0.32 : 0.1;

  let pages = base + expandedCount * expandedFactor + lowerPriorityCount * lowerPriorityFactor;
  if (includeDetailedScoringAppendix) pages += expandedCount * 0.5;
  if (includeEvidenceTrace) pages += expandedCount * 0.35;
  if (includeDocumentChecklist) pages += normalizedMode === 'full_all_grants' ? Math.max(2, expandedCount * 0.35) : expandedCount * 0.2;
  if (normalizedMode === 'full_all_grants') pages += 2.0; // added durable-reference sections
  return Math.max(1, Math.round(pages));
}

export function buildExportSummaryStats({ grantsAnalyzed, expandedGrants, lowerPriorityGrants, mode, companyName }) {
  const scores = expandedGrants.map((g) => g.alignmentScore).filter((n) => Number.isFinite(n));
  const highConfidenceMatches = expandedGrants.filter((g) => g.confidenceBand === 'Evidence Ready').length;
  const likelyMatches = expandedGrants.filter((g) => g.status === 'likely_fit').length;
  const top = expandedGrants[0];
  return {
    companyName: companyName || 'Not available',
    totalAnalyzed: grantsAnalyzed.length,
    grantsRecommended: expandedGrants.length,
    grantsIncluded: expandedGrants.length,
    topMatchedGrant: top?.displayName || 'Not available',
    averageMatchScore: scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null,
    highConfidenceMatches,
    likelyMatches,
    excludedLowPriorityCount: lowerPriorityGrants.length,
    expandedCount: expandedGrants.length,
    compactCount: lowerPriorityGrants.length,
    selectedMode: mode,
  };
}

export function validatePdfExportData(vm) {
  const issues = [];
  const expanded = Array.isArray(vm?.expandedGrants)
    ? vm.expandedGrants
    : Array.isArray(vm?.topGrantProfiles)
      ? vm.topGrantProfiles
      : [];
  const compact = Array.isArray(vm?.lowerPriority) ? vm.lowerPriority : [];
  if (!vm) issues.push('Export view model not available.');
  if (!expanded.length && !compact.length) issues.push('No grants meet the selected export filters.');

  const ids = new Set();
  for (const g of expanded) {
    if (!g.canonicalGrantKey) issues.push('Grant missing canonical key.');
    if (g.canonicalGrantKey && ids.has(g.canonicalGrantKey)) issues.push('Duplicate canonical grants detected.');
    if (g.canonicalGrantKey) ids.add(g.canonicalGrantKey);
    if (!Number.isFinite(g.alignmentScore)) issues.push(`Invalid score for ${g.displayName || 'grant'}.`);
    if (g.status === 'strong_match' && g.strategicRelevanceScore < 65) issues.push(`Strong match relevance mismatch for ${g.displayName || 'grant'}.`);
  }

  const serialized = JSON.stringify(vm || {});
  if (PLACEHOLDER_PATTERNS.some((re) => re.test(serialized))) {
    issues.push('Placeholder or dummy strings detected in export content.');
  }
  if (/[A-Za-z]\u00b7[A-Za-z]\u00b7[A-Za-z]/.test(serialized)) {
    issues.push('Character-split grant names detected.');
  }

  return {
    valid: issues.length === 0,
    issues,
  };
}

export function buildPDFExportViewModel({
  grants = [],
  mode = 'standard',
  minimumScoreThreshold = 0,
  includeLowerPriority = true,
  includeFundingSnapshots = true,
  includeDocumentChecklist = true,
  includeDetailedScoringAppendix = false,
  includeEvidenceTrace = false,
  includeScoringAppendix = false,
  companyContext = {},
  rankingKeys = [],
}) {
  const isExecutiveMode = mode === 'executive';
  const isStandardMode = mode === 'standard';
  const isFullMode = mode === 'full';

  // Backend ranking (recommended + backups, ordered) is the source of truth when supplied.
  const rankingIndex = (grant) => {
    if (!Array.isArray(rankingKeys) || rankingKeys.length === 0) return -1;
    const candidates = [grant?.code, grant?.grantId, grant?.grant_id, grant?.id, grant?.acronym, grant?.key, grant?.name, grant?.grantFullName]
      .map((c) => String(c || '').trim().toLowerCase())
      .filter(Boolean);
    return rankingKeys.findIndex((key) => candidates.includes(String(key || '').trim().toLowerCase()));
  };

  const normalized = grants
    .map((grant, originalIndex) => {
      const alignmentScore = Number(grant?.score ?? grant?.alignmentScore ?? 0);
      const confidenceScore = Number(grant?.certainty || grant?.confidence_score || grant?.confidenceScore || 0);
      const blocked = isGrantBlocked(grant);
      const strategicRelevanceScore = calculateStrategicRelevance(grant, companyContext);
      const status = classifyGrantStatus({ alignmentScore, strategicRelevanceScore, confidenceScore, blocked });
      const confidenceBand =
        confidenceScore >= 75
          ? 'Evidence Ready'
          : confidenceScore >= 60
            ? 'Requires Validation'
            : confidenceScore >= 45
              ? 'Preliminary Fit'
              : 'Low Evidence Support';
      const compositeRankScore = (alignmentScore * 0.6 + strategicRelevanceScore * 0.3 + confidenceScore * 0.1) * (blocked ? 0.5 : 1);
      return {
        grant,
        originalIndex,
        rankingIndex: rankingIndex(grant),
        canonicalGrantKey: getCanonicalGrantKey(grant),
        displayName: getGrantDisplayName(grant),
        alignmentScore,
        confidenceScore,
        confidenceBand,
        strategicRelevanceScore,
        strategicFitLabel: fitBandLabel(strategicRelevanceScore, { pdf: true }),
        blocked,
        blockReason: String(grant?.blockReason || grant?.block_reason || '').trim() || null,
        status,
        compositeRankScore,
      };
    })
    .filter((g) => Number.isFinite(g.alignmentScore));

  const deduped = dedupeGrants(normalized);
  const merged = mergeGrantVariants(deduped);
  const relevant = isFullMode
    ? merged.filter((g) => g.alignmentScore >= minimumScoreThreshold)
    : merged.filter((g) => g.strategicRelevanceScore >= 35 && g.alignmentScore >= minimumScoreThreshold);

  const sorted = [...relevant].sort((a, b) => {
    // Blocked grants always sink below unblocked ones.
    if (a.blocked !== b.blocked) return a.blocked ? 1 : -1;
    const ra = a.rankingIndex;
    const rb = b.rankingIndex;
    if (ra !== -1 || rb !== -1) {
      if (ra === -1) return 1;
      if (rb === -1) return -1;
      return ra - rb;
    }
    if ((b.alignmentScore || 0) !== (a.alignmentScore || 0)) return (b.alignmentScore || 0) - (a.alignmentScore || 0);
    if ((b.confidenceScore || 0) !== (a.confidenceScore || 0)) return (b.confidenceScore || 0) - (a.confidenceScore || 0);
    return (a.originalIndex || 0) - (b.originalIndex || 0);
  });
  const cap = isExecutiveMode ? 5 : isStandardMode ? 10 : sorted.length;

  // Blocked grants are never "expanded" recommendations; they are listed as lower priority with their block reason.
  const expandedBase = (isFullMode || isStandardMode)
    ? sorted.filter((g) => !g.blocked)
    : sorted.filter((g) => g.status !== 'excluded' && !g.blocked);
  const expandedGrants = expandedBase.slice(0, cap);
  const lowerPriorityAll = sorted
    .filter((g) => !expandedGrants.includes(g))
    .concat(merged.filter((g) => !relevant.includes(g)))
    .filter((g) => (isFullMode ? true : g.status !== 'excluded' || g.blocked));
  const lowerPriorityGrants = includeLowerPriority ? lowerPriorityAll : [];

  const stats = buildExportSummaryStats({
    grantsAnalyzed: merged,
    expandedGrants,
    lowerPriorityGrants,
    mode,
    companyName: companyContext.companyName,
  });

  const estimatedPageCount = estimatePdfPageCount({
    mode,
    expandedCount: expandedGrants.length,
    lowerPriorityCount: lowerPriorityGrants.length,
    includeDetailedScoringAppendix,
    includeEvidenceTrace,
    includeDocumentChecklist,
  });

  const vm = {
    meta: {
      companyName: companyContext.companyName || 'Not available',
      industry: companyContext.industry || 'Not available',
      assessmentDate: companyContext.assessmentDate || new Date().toISOString(),
      assessmentId: companyContext.assessmentId || 'Not available',
      exportMode: mode,
      totalAnalyzed: merged.length,
      totalIncluded: expandedGrants.length + lowerPriorityGrants.length,
    },
    clientSnapshot: {
      companyName: companyContext.companyName || 'Not available',
      industry: companyContext.industry || 'Not available',
      employees: companyContext.employees ?? 'Not available',
      annualRevenue: formatCurrency(companyContext.annualRevenue),
      localOwnership: formatPercent(companyContext.localOwnership),
      projectType: companyContext.projectType || 'Not available',
      projectBudget: formatCurrency(companyContext.projectBudget),
      projectScope: companyContext.projectScope || 'Not available',
      availableDocuments: Array.isArray(companyContext.availableDocuments) ? companyContext.availableDocuments : [],
    },
    summary: {
      topRecommendations: expandedGrants,
      strategicInsights: [],
      readinessGaps: [],
      nextSteps: [],
    },
    topGrantProfiles: expandedGrants,
    expandedGrants,
    lowerPriority: lowerPriorityGrants,
    appendix: {
      includeDetailedScoringAppendix,
      includeEvidenceTrace,
      includeScoringAppendix,
      includeDocumentChecklist,
      includeFundingSnapshots,
    },
    stats: {
      ...stats,
      estimatedPageCount,
    },
    controls: {
      minimumScoreThreshold,
      includeLowerPriority,
      includeDetailedScoringAppendix,
      includeEvidenceTrace,
      includeDocumentChecklist,
      includeFundingSnapshots,
    },
  };

  return {
    ...vm,
    validation: validatePdfExportData(vm),
  };
}
